import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFViewer,
  Image,
} from "@react-pdf/renderer";
import image from "../../../assets/img/marifer.png";
import moment from "moment";

const C = {
  pink: "#f43f5e",
  pinkLight: "#f9c6cf",
  gray: "#e6e6e6",
  dark: "#4b5563",
  white: "#ffffff",
  altRow: "#fdf2f4",
};

const styles = StyleSheet.create({
  viewer: { width: "100%", height: 620 },
  page: { fontSize: 9, padding: 20, fontFamily: "Helvetica" },

  /* Header */
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingBottom: 8,
    marginBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: C.pink,
    borderBottomStyle: "solid",
  },
  logo: { width: 65, height: 65, objectFit: "cover" },
  headerText: { flex: 1, paddingLeft: 14 },
  title: { fontSize: 13, color: C.pink, textTransform: "uppercase" },
  subtitle: { fontSize: 8, color: C.dark, marginTop: 3 },

  /* Summary */
  summaryRow: { flexDirection: "row", marginBottom: 12 },
  summaryBox: {
    flex: 1,
    marginRight: 8,
    borderWidth: 1,
    borderColor: C.pink,
    borderStyle: "solid",
    padding: 6,
    alignItems: "center",
  },
  summaryBoxLast: {
    flex: 1,
    borderWidth: 1,
    borderColor: C.pink,
    borderStyle: "solid",
    padding: 6,
    alignItems: "center",
  },
  summaryValue: { fontSize: 13, color: C.pink },
  summaryLabel: { fontSize: 7, color: C.dark, marginTop: 2, textTransform: "uppercase" },

  /* Table */
  tableWrap: { borderWidth: 1, borderColor: "#ddd", borderStyle: "solid" },
  tHead: {
    flexDirection: "row",
    backgroundColor: C.pinkLight,
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
    borderBottomStyle: "solid",
  },
  tRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    borderBottomStyle: "solid",
  },
  tRowAlt: {
    flexDirection: "row",
    backgroundColor: C.altRow,
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    borderBottomStyle: "solid",
  },
  tTotals: {
    flexDirection: "row",
    backgroundColor: C.pinkLight,
    borderTopWidth: 2,
    borderTopColor: C.pink,
    borderTopStyle: "solid",
  },
  cell: {
    padding: "3px 4px",
    borderRightWidth: 1,
    borderRightColor: "#ddd",
    borderRightStyle: "solid",
  },
  cellLast: { padding: "3px 4px" },

  /* Footer */
  footer: {
    position: "absolute",
    bottom: 10,
    left: 20,
    right: 20,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 7,
    color: C.dark,
    borderTopWidth: 1,
    borderTopColor: "#eee",
    borderTopStyle: "solid",
    paddingTop: 4,
  },
});

// Anchos de columnas (landscape LETTER ≈ 756pt usable)
const W = {
  num:    "4%",
  codigo: "12%",
  tela:   "27%",
  color:  "14%",
  kg:     "9%",
  metros: "9%",
  fecha:  "12%",
  entrada:"13%",
};

const InputsReportPdf = ({ rolls, dateFrom, dateTo, reportType }) => {
  const totalKg     = rolls.reduce((s, r) => s + (parseFloat(r.totalKg) || 0), 0);
  const totalMeters = rolls.reduce((s, r) => s + (parseFloat(r.meters)  || 0), 0);
  const fromStr = moment(dateFrom).format("DD/MM/YYYY");
  const toStr   = moment(dateTo).format("DD/MM/YYYY");
  const nowStr  = moment().format("DD/MM/YYYY HH:mm");
  const isVoid  = reportType === "void";

  return (
    <PDFViewer style={styles.viewer}>
      <Document>
        <Page size="LETTER" orientation="landscape" style={styles.page}>

          {/* ── Header ── */}
          <View style={styles.headerRow} fixed>
            <Image style={styles.logo} src={image} />
            <View style={styles.headerText}>
              <Text style={styles.title}>
                {isVoid ? "Reporte de Rollos Dados de Baja" : "Reporte de Entradas de Rollos"}
              </Text>
              <Text style={styles.subtitle}>Período: {fromStr} — {toStr}</Text>
              <Text style={[styles.subtitle, { marginTop: 1 }]}>Generado: {nowStr}</Text>
            </View>
          </View>

          {/* ── Resumen ── */}
          <View style={styles.summaryRow}>
            <View style={styles.summaryBox}>
              <Text style={styles.summaryValue}>{rolls.length}</Text>
              <Text style={styles.summaryLabel}>Total Rollos</Text>
            </View>
            <View style={styles.summaryBox}>
              <Text style={styles.summaryValue}>{totalKg.toFixed(2)}</Text>
              <Text style={styles.summaryLabel}>Total Kg</Text>
            </View>
            <View style={styles.summaryBoxLast}>
              <Text style={styles.summaryValue}>{totalMeters.toFixed(2)}</Text>
              <Text style={styles.summaryLabel}>Total Metros</Text>
            </View>
          </View>

          {/* ── Tabla ── */}
          <View style={styles.tableWrap}>
            {/* Encabezado */}
            <View style={styles.tHead} fixed>
              <Text style={[styles.cell, { width: W.num }]}>#</Text>
              <Text style={[styles.cell, { width: W.codigo }]}>Código</Text>
              <Text style={[styles.cell, { width: W.tela }]}>Tela</Text>
              <Text style={[styles.cell, { width: W.color }]}>Color</Text>
              <Text style={[styles.cell, { width: W.kg, textAlign: "right" }]}>Kg</Text>
              <Text style={[styles.cell, { width: W.metros, textAlign: "right" }]}>Metros</Text>
              <Text style={[styles.cell, { width: W.fecha }]}>Fecha Entrada</Text>
              <Text style={[styles.cellLast, { width: W.entrada }]}>Entrada #</Text>
            </View>

            {/* Filas */}
            {rolls.map((roll, i) => {
              const rollDate  = roll._inputDate || roll.input?.date || roll.createdAt;
              const dateStr   = rollDate ? moment(rollDate).format("DD/MM/YYYY") : "-";
              const entradaId = roll._inputId ? `#${roll._inputId}` : (roll.input?.id ? `#${roll.input.id}` : "-");
              const RowStyle  = i % 2 === 0 ? styles.tRow : styles.tRowAlt;
              return (
                <View key={roll.id} style={RowStyle} wrap={false}>
                  <Text style={[styles.cell, { width: W.num, color: C.dark }]}>{i + 1}</Text>
                  <Text style={[styles.cell, { width: W.codigo }]}>{roll.code || "-"}</Text>
                  <Text style={[styles.cell, { width: W.tela }]}>{roll.cloth?.name || "-"}</Text>
                  <Text style={[styles.cell, { width: W.color }]}>{roll.color || "-"}</Text>
                  <Text style={[styles.cell, { width: W.kg, textAlign: "right" }]}>
                    {parseFloat(roll.totalKg || 0).toFixed(2)}
                  </Text>
                  <Text style={[styles.cell, { width: W.metros, textAlign: "right" }]}>
                    {parseFloat(roll.meters || 0).toFixed(2)}
                  </Text>
                  <Text style={[styles.cell, { width: W.fecha }]}>{dateStr}</Text>
                  <Text style={[styles.cellLast, { width: W.entrada }]}>{entradaId}</Text>
                </View>
              );
            })}

            {/* Totales */}
            <View style={styles.tTotals}>
              <Text style={[styles.cell, { width: W.num }]}></Text>
              <Text style={[styles.cell, { width: W.codigo }]}></Text>
              <Text style={[styles.cell, { width: W.tela, color: C.pink }]}>TOTALES</Text>
              <Text style={[styles.cell, { width: W.color }]}></Text>
              <Text style={[styles.cell, { width: W.kg, textAlign: "right", color: C.pink }]}>
                {totalKg.toFixed(2)}
              </Text>
              <Text style={[styles.cell, { width: W.metros, textAlign: "right", color: C.pink }]}>
                {totalMeters.toFixed(2)}
              </Text>
              <Text style={[styles.cell, { width: W.fecha }]}></Text>
              <Text style={[styles.cellLast, { width: W.entrada }]}></Text>
            </View>
          </View>

          {/* ── Pie de página ── */}
          <View style={styles.footer} fixed>
            <Text>Marisoft — Reporte de Rollos</Text>
            <Text render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`} />
          </View>
        </Page>
      </Document>
    </PDFViewer>
  );
};

export default InputsReportPdf;
